<script setup lang="ts">
/**
 * FooterWechatLightbox — 微信二维码点击放大 lightbox
 * - WAI-ARIA 模式对话框
 * - Esc 关闭 / 点外部关闭 / focus trap
 */
import { onMounted, onUnmounted, ref, nextTick } from 'vue'

const props = defineProps<{
  src: string
  alt: string
  title?: string
  hint?: string
}>()

const open = ref(false)
const dialogRef = ref<HTMLDivElement | null>(null)
const triggerRef = ref<HTMLButtonElement | null>(null)

function show() {
  open.value = true
  nextTick(() => {
    dialogRef.value?.focus()
  })
}

function hide() {
  open.value = false
  triggerRef.value?.focus()
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value) return
  if (e.key === 'Escape') {
    e.preventDefault()
    hide()
  }
  if (e.key === 'Tab' && dialogRef.value) {
    const focusable = dialogRef.value.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }
}

function onBackdropClick(e: MouseEvent) {
  if (e.target === e.currentTarget) hide()
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <button
    ref="triggerRef"
    type="button"
    class="wechat-trigger"
    :aria-label="alt"
    @click="show"
  >
    <img :src="src" :alt="alt" class="wechat-trigger__img" />
    <span v-if="title" class="wechat-trigger__title">{{ title }}</span>
    <span v-if="hint" class="wechat-trigger__hint">{{ hint }}</span>
  </button>

  <Teleport to="body">
    <div
      v-if="open"
      ref="dialogRef"
      class="lightbox"
      role="dialog"
      aria-modal="true"
      :aria-label="title || alt"
      tabindex="-1"
      @click="onBackdropClick"
    >
      <div class="lightbox__inner">
        <button
          type="button"
          class="lightbox__close"
          aria-label="Close"
          @click="hide"
        >
          ✕
        </button>
        <img :src="src" :alt="alt" class="lightbox__img" />
        <p v-if="title" class="lightbox__title">{{ title }}</p>
        <p v-if="hint" class="lightbox__hint">{{ hint }}</p>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.wechat-trigger {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: var(--yfb-sp-1);
  padding: var(--yfb-sp-2);
  background-color: transparent;
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  cursor: pointer;
  font-family: inherit;
  color: var(--yfb-ink);
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.wechat-trigger:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 12px var(--yfb-glow);
}

.wechat-trigger__img {
  display: block;
  width: 80px;
  height: 80px;
  object-fit: contain;
  background-color: var(--yfb-surface);
  padding: var(--yfb-sp-1);
  border-radius: var(--yfb-radius-sm);
}

.wechat-trigger__title {
  font-size: var(--yfb-fs-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
}

.wechat-trigger__hint {
  font-size: var(--yfb-fs-xs);
  opacity: 0.7;
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--yfb-veil);
  padding: var(--yfb-sp-6);
}

.lightbox__inner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--yfb-sp-4);
  background-color: var(--yfb-glass-bg-strong);
  -webkit-backdrop-filter: blur(var(--yfb-glass-blur));
  backdrop-filter: blur(var(--yfb-glass-blur));
  border: 1px solid var(--yfb-glass-border);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-3);
  padding: var(--yfb-sp-7);
  max-width: 360px;
  width: 100%;
}

.lightbox__close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out;
}

.lightbox__close:hover {
  border-color: var(--yfb-accent);
  box-shadow: 0 0 12px var(--yfb-glow);
}

.lightbox__img {
  display: block;
  width: 256px;
  height: 256px;
  object-fit: contain;
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-md);
}

.lightbox__title {
  margin: 0;
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  text-transform: uppercase;
  color: var(--yfb-ink);
}

.lightbox__hint {
  margin: 0;
  font-size: var(--yfb-fs-sm);
  color: var(--yfb-ink-soft);
}
</style>
